import { FormCoordinatorBadge } from '@framework/form';

import { Space, Tabs, TabsProps } from 'antd';
import clx from 'classnames';
import produce from 'immer';
import React, { useEffect, useMemo, useState } from 'react';
import { VerticalSpace } from '../space/VerticalSpace';
import { ContentLayoutWrapper } from './ContentLayoutWrapper';
import { IPageLayoutBaseProps, PageLayoutBase } from './PageLayoutBase';
import {
  AffixAdminHeader,
  AffixAdminHeaderProps,
} from './header/AffixAdminHeader';
import {
  CorePageLayoutTabsPrefix,
  useTabsTabIndexReset,
} from './hooks/useTabsTabIndexReset';

type Item<T extends any[] | undefined> = T extends Array<infer I> ? I : never;

export type AdminRichTab = Item<TabsProps['items']> & {
  /**
   * Se viene fornito un formId, il tab sarà evidenziato come 'dirty' quando
   * il form è stato modificato.
   */
  formId?: string;
  /**
   * Se true, il tab occuperà tutta la larghezza
   */
  fullWidth?: boolean;
};

export interface IRichPageLayoutProps
  extends Omit<AffixAdminHeaderProps, 'children' | 'onAffixChange' | 'tabs'> {
  tabs?: AdminRichTab[];
  children?: React.ReactNode;
  defaultActiveTab?: string;
  activeTab?: string;

  pageProps?: Omit<IPageLayoutBaseProps, 'children'>;
  removeBodyPadding?: boolean;

  /**  Per gestire la larghezza del footer */
  // hasSider?: boolean;
  tabKeyHasSider?: string[];
}

/**
 * Layout principale dell'applicativo lato Admin con menu superiore,
 * Fascia del titolo Affixed e contenuto 'boxed'
 *
 * ex AdminPageLayout
 *
 */
export function RichPageLayout(props: IRichPageLayoutProps) {
  const {
    children,
    tabs,
    pageProps,
    removeBodyPadding,
    tabBarExtraContent,
    breadcrumb,
    defaultActiveTab,
    activeTab: currentActiveTab,
    ...headerProps
  } = props;
  const [affixed, setAffixed] = useState<boolean | undefined>();

  // Gestione interna in caso di tabs
  const [activeTab, setActiveTab] = useState<string>(
    defaultActiveTab ?? tabs?.[0].key ?? '',
  );

  useEffect(() => {
    if (currentActiveTab) {
      setActiveTab(currentActiveTab);
    }
  }, [currentActiveTab]);

  const hasTabs = tabs && tabs.length > 0;

  // Elimino il contenuto dei tab per evitare il doppio rendering.
  const tabsMenu = useMemo(
    () =>
      props.tabs?.map(
        produce(tab => {
          delete tab.children;

          // Se il tab ha un formId, aggiungo il badge di 'dirty'
          // automaticamente.
          if (tab.formId) {
            tab.label = (
              <Space>
                {tab.label}
                <FormCoordinatorBadge formId={tab.formId} />
              </Space>
            );
          }
        }),
      ),
    [props.tabs],
  );
  const fullWidthTab = useMemo(
    () => tabs?.find(tab => tab.key === activeTab)?.fullWidth,
    [tabs, activeTab],
  );

  // Gestiamo il cambio di tab, eventualmente segnalandolo anche al padre
  const onTabChange = (key: string) => {
    setActiveTab(key);
    props.onTabChange?.(key);
  };

  // Reset tabIndex dei tab content (vedi l'hook per il dettaglio)
  useTabsTabIndexReset(tabs, activeTab);

  return (
    <PageLayoutBase
      noPadding
      {...pageProps}
      className={clx(props.className, { 'title-affixed': affixed })}
    >
      <AffixAdminHeader
        {...headerProps}
        breadcrumb={breadcrumb}
        tabs={tabsMenu}
        onAffixChange={affixed => setAffixed(affixed)}
        onTabChange={onTabChange}
        tabBarExtraContent={tabBarExtraContent}
        activeTab={activeTab}
      />
      <ContentLayoutWrapper
        boxed={!fullWidthTab}
        // padded={removeBodyPadding ? false : !fullWidthTab}
        topPadding={removeBodyPadding ? false : !fullWidthTab}
      >
        {hasTabs && (
          <Tabs
            id={`${CorePageLayoutTabsPrefix}main`}
            animated={false}
            renderTabBar={t => {
              return <div id={`${t.id}-tab-${t.activeKey}`} />;
            }}
            items={tabs}
            activeKey={activeTab}
          />
        )}
        {props.children}
      </ContentLayoutWrapper>
      {affixed && <VerticalSpace size={props.pageProps?.footer ? 10 : 100} />}
    </PageLayoutBase>
  );
}
